import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import GrantEditor from "./GrantEditor";
import {
  approveIdentity,
  createDashboardUser,
  fetchGrantCatalog,
  impersonateIdentity,
  listIdentities,
  patchIdentity,
} from "./api";
import { FoxlineRowActions, InviteOpsUser } from "./FoxlineAccess";
import { PRESET_LABELS, loginKindLabel } from "./identityScope";
import { canImpersonateUsers } from "./access";
import {
  grantsIncludeApp,
  openImpersonationHandoff,
  osAppUrl,
  stashCurrentOpsSessionIfNeeded,
} from "./impersonation";
import { setOpsToken } from "../api/client";
import { opsUserFromAuthPayload } from "../lib/userDisplay";
import { landingPageForUser } from "../routing/paths";
import { Icon } from "../ui/Icon";
import { identityMatchesQuery } from "./identitySearch";

function formatLastLogin(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
}

function grantSummary(grants) {
  if (!grants || !grants.length) return "No grants";
  const bits = grants.map((g) => `${g.app}/${g.module}:${g.action}`);
  if (bits.length <= 4) return bits.join(" · ");
  return `${bits.slice(0, 4).join(" · ")} +${bits.length - 4}`;
}

function t(lang, en, gr) {
  return lang === "gr" ? gr : en;
}

export default function IdentityPage({ lang, sessionUser, onLocalImpersonation }) {
  const [users, setUsers] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("user");
  const [creating, setCreating] = useState(false);

  const [busyId, setBusyId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [query, setQuery] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rows, cat] = await Promise.all([listIdentities(), fetchGrantCatalog()]);
      setUsers(rows);
      setCatalog(cat);
    } catch (e) {
      setError(e.message || t(lang, "Failed to load users", "Αποτυχία φόρτωσης χρηστών"));
    } finally {
      setLoading(false);
    }
  }, [lang]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function onCreate(e) {
    e.preventDefault();
    setNotice(null);
    setError(null);
    setCreating(true);
    try {
      await createDashboardUser({
        email: email.trim(),
        name: name.trim() || null,
        role,
      });
      setEmail("");
      setName("");
      setRole("user");
      setNotice(t(lang, "HQ user provisioned as active. They can sign in with Google.", "Ο χρήστης HQ ενεργοποιήθηκε. Μπορεί να συνδεθεί με Google."));
      await refresh();
    } catch (err) {
      setError(
        err.status === 409
          ? t(lang, "Email already exists.", "Το email υπάρχει ήδη.")
          : err.message || t(lang, "Failed to create user", "Αποτυχία δημιουργίας χρήστη")
      );
    } finally {
      setCreating(false);
    }
  }

  async function onApprove(user, payload) {
    setError(null);
    setNotice(null);
    setBusyId(user.id);
    try {
      await approveIdentity(user.id, payload);
      setNotice(t(lang, `Approved ${user.email}`, `Εγκρίθηκε ${user.email}`));
      setApprovingId(null);
      await refresh();
    } catch (err) {
      setError(err.message || t(lang, "Failed to approve user", "Αποτυχία έγκρισης"));
    } finally {
      setBusyId(null);
    }
  }

  async function onSaveGrants(user, payload) {
    setError(null);
    setNotice(null);
    setBusyId(user.id);
    try {
      await patchIdentity(user.id, payload);
      setNotice(t(lang, `Updated access for ${user.email}`, `Ενημερώθηκε η πρόσβαση για ${user.email}`));
      setEditingId(null);
      await refresh();
    } catch (err) {
      setError(err.message || t(lang, "Failed to save grants", "Αποτυχία αποθήκευσης"));
    } finally {
      setBusyId(null);
    }
  }

  async function onDeactivate(user) {
    setError(null);
    setNotice(null);
    if (user.status === "active") {
      const ok = window.confirm(
        t(lang, `Deactivate ${user.email}? They lose Ops and OS access.`, `Απενεργοποίηση ${user.email}; Χάνει πρόσβαση σε Ops και OS.`)
      );
      if (!ok) return;
    }
    setBusyId(user.id);
    try {
      await patchIdentity(user.id, {
        status: user.status === "deactivated" ? "active" : "deactivated",
      });
      await refresh();
    } catch {
      setError(t(lang, "Failed to update status", "Αποτυχία ενημέρωσης κατάστασης"));
    } finally {
      setBusyId(null);
    }
  }

  async function onViewAs(user, product) {
    setError(null);
    setNotice(null);
    if (sessionUser?.email && user.email === sessionUser.email) return;
    const ok = window.confirm(
      product === "ops"
        ? t(
            lang,
            `View Ops as ${user.email}? Your admin session is saved until you stop.`,
            `Προβολή Ops ως ${user.email}; Η συνεδρία διαχειριστή αποθηκεύεται μέχρι να σταματήσετε.`
          )
        : t(
            lang,
            `Open Stiq OS as ${user.email}? A new tab will use their OS access.`,
            `Άνοιγμα Stiq OS ως ${user.email}; Νέα καρτέλα με την πρόσβασή τους.`
          )
    );
    if (!ok) return;
    setBusyId(user.id);
    try {
      const session = await impersonateIdentity(user.id, product);
      if (product === "ops") {
        stashCurrentOpsSessionIfNeeded();
        if (session.access_token) setOpsToken(session.access_token);
        const next = opsUserFromAuthPayload(session);
        if (!next) throw new Error(t(lang, "Invalid session", "Μη έγκυρη συνεδρία"));
        const { access_token: _at, token_type: _tt, product: _p, status: _s, ...rest } = next;
        if (onLocalImpersonation) onLocalImpersonation(rest, landingPageForUser(rest));
        return;
      }
      if (!session.access_token) throw new Error(t(lang, "Invalid session", "Μη έγκυρη συνεδρία"));
      openImpersonationHandoff(osAppUrl(), session.access_token);
      setNotice(t(lang, `Opened OS as ${user.email}`, `Άνοιξε OS ως ${user.email}`));
    } catch (err) {
      setError(err.message || t(lang, "Failed to view as this user", "Αποτυχία προβολής ως χρήστης"));
    } finally {
      setBusyId(null);
    }
  }

  const pending = users.filter((u) => u.status === "pending");
  const visible = useMemo(
    () => users.filter((u) => identityMatchesQuery(u, query)),
    [users, query]
  );
  const searching = query.trim().length > 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div>
        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "var(--text-primary)" }}>
          {t(lang, "Identity", "Ταυτότητα")}
        </h1>
        <p style={{ margin: "6px 0 0", fontSize: 13, color: "var(--text-muted)", maxWidth: 640 }}>
          {t(
            lang,
            "Login kind follows the email (@stiq.gr = HQ, otherwise Ops-only). Assign an Ops role: Crew, Chef, or Store manager (T1) with brand then store. Foxline job title is payroll, not access.",
            "Το είδος σύνδεσης ακολουθεί το email (@stiq.gr = HQ, αλλιώς μόνο Ops). Ορίστε ρόλο Ops: Crew, Chef ή Store manager (T1) με μάρκα και κατάστημα. Ο τίτλος Foxline είναι μισθοδοσία, όχι πρόσβαση.",
          )}
        </p>
      </div>

      {(error || notice) && (
        <div
          role="status"
          style={{
            borderRadius: 12,
            border: `1px solid ${error ? "#FECACA" : "var(--green)"}`,
            background: error ? "#FEF2F2" : "var(--green-light, #ECFDF5)",
            color: error ? "#991B1B" : "var(--green)",
            padding: "10px 14px",
            fontSize: 13,
          }}
        >
          {error || notice}
        </div>
      )}

      {pending.length > 0 && catalog && (
        <div
          style={{
            borderRadius: 14,
            border: "1px solid #F59E0B",
            background: "rgba(245, 158, 11, 0.08)",
            padding: 16,
          }}
        >
          <h2 style={{ margin: "0 0 12px", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#B45309" }}>
            {t(lang, `Pending approval (${pending.length})`, `Εκκρεμεί έγκριση (${pending.length})`)}
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {pending.map((u) => (
              <div key={u.id} style={{ borderTop: "1px solid var(--border)", paddingTop: 12 }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontSize: 14, color: "var(--text-primary)" }}>{u.email}</div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                      {u.identity_kind === "limited"
                        ? t(lang, "Ops login · personal email", "Σύνδεση Ops · προσωπικό email")
                        : t(lang, "HQ login · @stiq.gr", "Σύνδεση HQ · @stiq.gr")}
                      {u.name ? ` · ${u.name}` : ""}
                    </div>
                  </div>
                  {approvingId !== u.id && (
                    <button
                      type="button"
                      className="btn-base btn-green btn-small"
                      onClick={() => setApprovingId(u.id)}
                    >
                      {t(lang, "Set access & approve", "Πρόσβαση και έγκριση")}
                    </button>
                  )}
                </div>
                {approvingId === u.id && (
                  <div style={{ marginTop: 12 }}>
                    <GrantEditor
                      catalog={catalog}
                      identityKind={u.identity_kind}
                      initialGrants={u.grants}
                      initialPreset={u.preset}
                      initialOpsRole={u.ops_role}
                      busy={busyId === u.id}
                      submitLabel={busyId === u.id ? t(lang, "Approving…", "Έγκριση…") : t(lang, "Approve", "Έγκριση")}
                      onCancel={() => setApprovingId(null)}
                      onSave={(payload) => void onApprove(u, payload)}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {catalog ? (
        <InviteOpsUser
          lang={lang}
          catalog={catalog}
          onDone={(msg) => {
            setNotice(msg);
            void refresh();
          }}
        />
      ) : null}

      <form
        onSubmit={onCreate}
        style={{
          borderRadius: 14,
          border: "1px solid var(--border)",
          background: "var(--surface)",
          padding: 16,
        }}
      >
        <h2 style={{ margin: "0 0 12px", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-muted)" }}>
          {t(lang, "Provision HQ user (@stiq.gr)", "Προσθήκη χρήστη HQ (@stiq.gr)")}
        </h2>
        <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: "var(--text-muted)" }}>
            Email
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@stiq.gr"
              style={{
                border: "1px solid var(--border)",
                background: "var(--surface2)",
                color: "var(--text-primary)",
                borderRadius: 10,
                padding: "8px 10px",
                fontSize: 13,
              }}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: "var(--text-muted)" }}>
            {t(lang, "Name", "Όνομα")}
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t(lang, "Optional", "Προαιρετικό")}
              style={{
                border: "1px solid var(--border)",
                background: "var(--surface2)",
                color: "var(--text-primary)",
                borderRadius: 10,
                padding: "8px 10px",
                fontSize: 13,
              }}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: "var(--text-muted)" }}>
            {t(lang, "HQ template", "Πρότυπο HQ")}
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              style={{
                border: "1px solid var(--border)",
                background: "var(--surface2)",
                color: "var(--text-primary)",
                borderRadius: 10,
                padding: "8px 10px",
                fontSize: 13,
              }}
            >
              <option value="user">HQ admin</option>
              <option value="admin">OS admin</option>
            </select>
          </label>
          <div style={{ display: "flex", alignItems: "flex-end" }}>
            <button type="submit" className="btn-base btn-green" disabled={creating} style={{ width: "100%" }}>
              {creating ? t(lang, "Adding…", "Προσθήκη…") : t(lang, "Provision", "Προσθήκη")}
            </button>
          </div>
        </div>
        <p style={{ margin: "10px 0 0", fontSize: 12, color: "var(--text-muted)" }}>
          {t(lang, "After provision, use Edit access to turn modules on or off.", "Μετά την προσθήκη, χρησιμοποιήστε Επεξεργασία πρόσβασης για τα modules.")}
        </p>
      </form>

      <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 11, color: "var(--text-muted)" }}>
        {t(lang, "Find a user", "Εύρεση χρήστη")}
        <span style={{ position: "relative", display: "block" }}>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t(lang, "Name, last name, email, or AFM", "Όνομα, επώνυμο, email ή ΑΦΜ")}
            className="admin-search"
            style={{ width: "100%", maxWidth: "100%", paddingLeft: 36 }}
          />
          <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", opacity: 0.35, pointerEvents: "none" }}>
            <Icon name="search" size={14} />
          </span>
        </span>
        {searching && (
          <span>
            {t(lang, `${visible.length} shown`, `${visible.length} εμφανίζονται`)}
          </span>
        )}
      </label>

      <div style={{ overflowX: "auto", borderRadius: 14, border: "1px solid var(--border)", background: "var(--surface)" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--border)", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-muted)" }}>
              <th style={{ padding: "12px 14px", fontWeight: 600 }}>Email</th>
              <th style={{ padding: "12px 14px", fontWeight: 600 }}>{t(lang, "Login", "Σύνδεση")}</th>
              <th style={{ padding: "12px 14px", fontWeight: 600 }}>{t(lang, "Status", "Κατάσταση")}</th>
              <th style={{ padding: "12px 14px", fontWeight: 600 }}>{t(lang, "Access", "Πρόσβαση")}</th>
              <th style={{ padding: "12px 14px", fontWeight: 600 }}>Foxline</th>
              <th style={{ padding: "12px 14px", fontWeight: 600 }}>{t(lang, "Last login", "Τελευταία είσοδος")}</th>
              <th style={{ padding: "12px 14px", fontWeight: 600 }}>{t(lang, "Actions", "Ενέργειες")}</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} style={{ padding: 32, textAlign: "center", color: "var(--text-muted)" }}>
                  {t(lang, "Loading users…", "Φόρτωση χρηστών…")}
                </td>
              </tr>
            )}
            {!loading && users.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: 32, textAlign: "center", color: "var(--text-muted)" }}>
                  {t(lang, "No identities yet.", "Δεν υπάρχουν ταυτότητες ακόμη.")}
                </td>
              </tr>
            )}
            {!loading && users.length > 0 && visible.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: 32, textAlign: "center", color: "var(--text-muted)" }}>
                  {t(lang, "No users match this search.", "Κανένας χρήστης δεν ταιριάζει.")}
                </td>
              </tr>
            )}
            {!loading &&
              visible.map((u) => (
                <Fragment key={u.id}>
                  <tr style={{ borderTop: "1px solid var(--border)" }}>
                    <td style={{ padding: "12px 14px", color: "var(--text-primary)" }}>
                      {u.email}
                      {u.name ? <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{u.name}</div> : null}
                    </td>
                    <td style={{ padding: "12px 14px", color: "var(--text-muted)" }}>
                      <div>{loginKindLabel(u.identity_kind)}</div>
                      <div style={{ fontSize: 11 }}>
                        {u.identity_kind === "limited" ? t(lang, "personal email", "προσωπικό email") : "@stiq.gr"}
                      </div>
                    </td>
                    <td style={{ padding: "12px 14px" }}>
                      <span
                        style={{
                          borderRadius: 999,
                          padding: "2px 8px",
                          fontSize: 10,
                          fontWeight: 700,
                          textTransform: "uppercase",
                          background:
                            u.status === "active"
                              ? "rgba(22, 163, 74, 0.15)"
                              : u.status === "pending"
                                ? "rgba(245, 158, 11, 0.18)"
                                : "rgba(239, 68, 68, 0.15)",
                          color:
                            u.status === "active"
                              ? "var(--green)"
                              : u.status === "pending"
                                ? "#B45309"
                                : "#B91C1C",
                        }}
                      >
                        {u.status}
                      </span>
                    </td>
                    <td style={{ padding: "12px 14px", fontSize: 11, color: "var(--text-muted)", maxWidth: 280 }}>
                      <div style={{ fontWeight: 600, color: "var(--text-primary)" }}>
                        {PRESET_LABELS[u.preset] || u.preset || t(lang, "Custom", "Προσαρμοσμένο")}
                      </div>
                      <div style={{ marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={grantSummary(u.grants)}>
                        {grantSummary(u.grants)}
                      </div>
                    </td>
                    <td style={{ padding: "12px 14px" }}>
                      {u.awaiting_password ? (
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#B45309", marginBottom: 6 }}>
                          {t(lang, "Waiting for password", "Εκκρεμεί κωδικός")}
                        </div>
                      ) : null}
                      <FoxlineRowActions
                        lang={lang}
                        user={u}
                        onDone={(msg, err) => {
                          if (err) setError(err);
                          else {
                            setError(null);
                            if (msg) setNotice(msg);
                            void refresh();
                          }
                        }}
                      />
                    </td>
                    <td style={{ padding: "12px 14px", fontFamily: "ui-monospace, monospace", fontSize: 12, color: "var(--text-muted)" }}>
                      {formatLastLogin(u.last_login_at)}
                    </td>
                    <td style={{ padding: "12px 14px" }}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {canImpersonateUsers(sessionUser) &&
                          u.status === "active" &&
                          u.email !== sessionUser?.email &&
                          grantsIncludeApp(u.grants, "ops") && (
                            <button
                              type="button"
                              className="btn-base btn-outline btn-small"
                              disabled={busyId === u.id}
                              onClick={() => void onViewAs(u, "ops")}
                            >
                              {t(lang, "View Ops as", "Προβολή Ops ως")}
                            </button>
                          )}
                        {canImpersonateUsers(sessionUser) &&
                          u.status === "active" &&
                          u.email !== sessionUser?.email &&
                          grantsIncludeApp(u.grants, "os") && (
                            <button
                              type="button"
                              className="btn-base btn-outline btn-small"
                              disabled={busyId === u.id}
                              onClick={() => void onViewAs(u, "os")}
                            >
                              {t(lang, "View OS as", "Προβολή OS ως")}
                            </button>
                          )}
                        {u.status !== "pending" && catalog && (
                          <button
                            type="button"
                            className="btn-base btn-outline btn-small"
                            onClick={() => setEditingId(editingId === u.id ? null : u.id)}
                          >
                            {editingId === u.id ? t(lang, "Close", "Κλείσιμο") : t(lang, "Edit access", "Επεξεργασία πρόσβασης")}
                          </button>
                        )}
                        {u.status !== "pending" && (
                          <button
                            type="button"
                            className="btn-base btn-outline btn-small"
                            disabled={busyId === u.id}
                            onClick={() => void onDeactivate(u)}
                          >
                            {u.status === "deactivated"
                              ? t(lang, "Reactivate", "Επανενεργοποίηση")
                              : t(lang, "Deactivate", "Απενεργοποίηση")}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {editingId === u.id && catalog && u.status !== "pending" && (
                    <tr style={{ borderTop: "1px solid var(--border)" }}>
                      <td colSpan={7} style={{ padding: 14 }}>
                        <GrantEditor
                          catalog={catalog}
                          identityKind={u.identity_kind}
                          initialGrants={u.grants}
                          initialPreset={u.preset}
                          initialOpsRole={u.ops_role}
                          busy={busyId === u.id}
                          submitLabel={busyId === u.id ? t(lang, "Saving…", "Αποθήκευση…") : t(lang, "Save access", "Αποθήκευση πρόσβασης")}
                          onCancel={() => setEditingId(null)}
                          onSave={(payload) => void onSaveGrants(u, payload)}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
